import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './EnergyExchange.css';

function EnergyExchange() {
    const { t } = useTranslation();
    const [selectedOption, setSelectedOption] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [transferResult, setTransferResult] = useState(null);
    
    // 能量地址
    const ENERGY_ADDRESS = 'THhs2pkCvF6gnqULQxAJ2ESpbqGDokk9cs';
    
    // 能量价格选项
    const energyOptions = [
        { 
            price: 2.55, 
            energy: 65000,
            description: t('energyOption1Desc')
        },
        { 
            price: 5, 
            energy: 131000,
            description: t('energyOption2Desc')
        }
    ];
    
    // 处理选择价格选项
    const handleSelectOption = (index) => {
        setSelectedOption(index);
        setTransferResult(null); // 清除之前的转账结果
    };
    
    // 执行转账
    const handleTransfer = async () => {
        if (selectedOption === null) {
            alert(t('selectEnergyPackageAlert'));
            return;
        }
        
        // 检查TronWeb是否可用
        if (!window.tronWeb || !window.tronWeb.ready) {
            alert(t('installTronlinkAlert'));
            return;
        }
        
        setIsLoading(true);
        setTransferResult(null);
        
        try {
            const selectedPrice = energyOptions[selectedOption].price;
            const amountSun = selectedPrice * 1000000; // 转换为SUN单位
            
            // 执行转账
            const result = await window.tronWeb.trx.sendTransaction(
                ENERGY_ADDRESS,
                amountSun
            );
            
            console.log('Transaction result:', result);
            
            if (result.result) {
                setTransferResult({
                    success: true,
                    txID: result.txid || result.transaction.txID,
                    message: t('paymentSuccessMessage', { price: selectedPrice })
                });
            } else {
                setTransferResult({
                    success: false,
                    message: t('transferFailedMessage')
                });
            }
        } catch (error) {
            console.error('Transfer error:', error);
            setTransferResult({
                success: false,
                message: error.message || t('transferFailedMessage')
            });
        } finally {
            setIsLoading(false);
        }
    };

    // 动画变体
    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                type: 'spring',
                stiffness: 200,
                damping: 20,
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 15
            }
        }
    };
    
    const itemVariants = {
        hidden: { opacity: 0, x: -10 },
        visible: { 
            opacity: 1, 
            x: 0,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 15
            }
        }
    };

    const buttonVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 15
            }
        },
        hover: { 
            scale: 1.03,
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
            transition: {
                type: 'spring',
                stiffness: 500,
                damping: 10
            }
        },
        tap: { scale: 0.97 }
    };
    
    // 新增机械动画效果
    const gearVariants = {
        animate: {
            rotate: 360,
            transition: {
                duration: 20,
                ease: "linear",
                repeat: Infinity
            }
        }
    };
    
    const pulseVariants = {
        animate: {
            scale: [1, 1.02, 1],
            opacity: [0.7, 0.9, 0.7],
            transition: {
                duration: 2,
                ease: "easeInOut",
                repeat: Infinity
            }
        }
    };
    
    const glowVariants = {
        animate: {
            boxShadow: [
                "0 0 5px rgba(52, 152, 219, 0.3)",
                "0 0 15px rgba(52, 152, 219, 0.5)",
                "0 0 5px rgba(52, 152, 219, 0.3)"
            ],
            transition: {
                duration: 3,
                ease: "easeInOut",
                repeat: Infinity
            }
        }
    };

    return (
        <motion.div 
            className="energy-exchange-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {/* 装饰性齿轮元素 */}
            <motion.div
                style={{
                    position: 'absolute',
                    top: '50px',
                    right: '15%',
                    width: '120px',
                    height: '120px',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%233498db' opacity='0.05'%3E%3Cpath d='M12 15a3 3 0 100-6 3 3 0 000 6z'/%3E%3Cpath d='M19.59 13.19c.12-.42.21-.83.25-1.26.04-.42-.15-.81-.54-.95l-2.18-.8c-.38-.14-.67-.56-.63-.99.01-.12.03-.23.03-.35 0-.12-.02-.23-.03-.35-.04-.43.25-.85.63-.99l2.18-.8c.39-.14.58-.53.54-.95-.04-.43-.13-.84-.25-1.26-.12-.41-.48-.69-.9-.65l-2.29.22c-.41.04-.85-.16-1.02-.55-.05-.12-.11-.23-.17-.34-.17-.34-.09-.82.21-1.12l1.66-1.65c.3-.3.32-.75.06-1.08-.26-.32-.55-.62-.87-.88-.33-.26-.78-.24-1.08.06l-1.65 1.66c-.3.3-.78.38-1.12.21-.11-.06-.22-.12-.34-.17-.39-.17-.59-.61-.55-1.02l.22-2.29c.04-.42-.24-.78-.65-.9-.42-.12-.83-.21-1.26-.25-.42-.04-.81.15-.95.54l-.8 2.18c-.14.38-.56.67-.99.63-.12-.01-.23-.03-.35-.03-.12 0-.23.02-.35.03-.43.04-.85-.25-.99-.63l-.8-2.18c-.14-.39-.53-.58-.95-.54-.43.04-.84.13-1.26.25-.41.12-.69.48-.65.9l.22 2.29c.04.41-.16.85-.55 1.02-.12.05-.23.11-.34.17-.34.17-.82.09-1.12-.21L3.56 2.89c-.3-.3-.75-.32-1.08-.06-.32.26-.62.55-.88.87-.26.33-.24.78.06 1.08l1.66 1.65c.3.3.38.78.21 1.12-.06.11-.12.22-.17.34-.17.39-.61.59-1.02.55l-2.29-.22c-.42-.04-.78.24-.9.65-.12.42-.21.83-.25 1.26-.04.42.15.81.54.95l2.18.8c.38.14.67.56.63.99-.01.12-.03.23-.03.35 0 .12.02.23.03.35.04.43-.25.85-.63.99l-2.18.8c-.39.14-.58.53-.54.95.04.43.13.84.25 1.26.12.41.48.69.9.65l2.29-.22c.41-.04.85.16 1.02.55.05.12.11.23.17.34.17.34.09.82-.21 1.12l-1.66 1.65c-.3.3-.32.75-.06 1.08.26.32.55.62.87.88.33.26.78.24 1.08-.06l1.65-1.66c.3-.3.78-.38 1.12-.21.11.06.22.12.34.17.39.17.59.61.55 1.02l-.22 2.29c-.04.42.24.78.65.9.42.12.83.21 1.26.25.42.04.81-.15.95-.54l.8-2.18c.14-.38.56-.67.99-.63.12.01.23.03.35.03.12 0 .23-.02.35-.03.43-.04.85.25.99.63l.8 2.18c.14.39.53.58.95.54.43-.04.84-.13 1.26-.25.41-.12.69-.48.65-.9l-.22-2.29c-.04-.41.16-.85.55-1.02.12-.05.23-.11.34-.17.34-.17.82-.09 1.12.21l1.65 1.66c.3.3.75.32 1.08.06.32-.26.62-.55.88-.87.26-.33.24-.78-.06-1.08l-1.66-1.65c-.3-.3-.38-.78-.21-1.12.06-.11.12-.22.17-.34.17-.39.61-.59 1.02-.55l2.29.22c.42.04.78-.24.9-.65z'/%3E%3C/svg%3E")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    zIndex: 1
                }}
                variants={gearVariants}
                animate="animate"
            />
            
            <motion.div
                style={{
                    position: 'absolute',
                    bottom: '100px',
                    left: '15%',
                    width: '80px',
                    height: '80px',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%232ecc71' opacity='0.05'%3E%3Cpath d='M12 15a3 3 0 100-6 3 3 0 000 6z'/%3E%3Cpath d='M19.59 13.19c.12-.42.21-.83.25-1.26.04-.42-.15-.81-.54-.95l-2.18-.8c-.38-.14-.67-.56-.63-.99.01-.12.03-.23.03-.35 0-.12-.02-.23-.03-.35-.04-.43.25-.85.63-.99l2.18-.8c.39-.14.58-.53.54-.95-.04-.43-.13-.84-.25-1.26-.12-.41-.48-.69-.9-.65l-2.29.22c-.41.04-.85-.16-1.02-.55-.05-.12-.11-.23-.17-.34-.17-.34-.09-.82.21-1.12l1.66-1.65c.3-.3.32-.75.06-1.08-.26-.32-.55-.62-.87-.88-.33-.26-.78-.24-1.08.06l-1.65 1.66c-.3.3-.78.38-1.12.21-.11-.06-.22-.12-.34-.17-.39-.17-.59-.61-.55-1.02l.22-2.29c.04-.42-.24-.78-.65-.9-.42-.12-.83-.21-1.26-.25-.42-.04-.81.15-.95.54l-.8 2.18c-.14.38-.56.67-.99.63-.12-.01-.23-.03-.35-.03-.12 0-.23.02-.35.03-.43.04-.85-.25-.99-.63l-.8-2.18c-.14-.39-.53-.58-.95-.54-.43.04-.84.13-1.26.25-.41.12-.69.48-.65.9l.22 2.29c.04.41-.16.85-.55 1.02-.12.05-.23.11-.34.17-.34.17-.82.09-1.12-.21L3.56 2.89c-.3-.3-.75-.32-1.08-.06-.32.26-.62.55-.88.87-.26.33-.24.78.06 1.08l1.66 1.65c.3.3.38.78.21 1.12-.06.11-.12.22-.17.34-.17.39-.61.59-1.02.55l-2.29-.22c-.42-.04-.78.24-.9.65-.12.42-.21.83-.25 1.26-.04.42.15.81.54.95l2.18.8c.38.14.67.56.63.99-.01.12-.03.23-.03.35 0 .12.02.23.03.35.04.43-.25.85-.63.99l-2.18.8c-.39.14-.58.53-.54.95.04.43.13.84.25 1.26.12.41.48.69.9.65l2.29-.22c.41-.04.85.16 1.02.55.05.12.11.23.17.34.17.34.09.82-.21 1.12l-1.66 1.65c-.3.3-.32.75-.06 1.08.26.32.55.62.87.88.33.26.78.24 1.08-.06l1.65-1.66c.3-.3.78-.38 1.12-.21.11.06.22.12.34.17.39.17.59.61.55 1.02l-.22 2.29c-.04.42.24.78.65.9.42.12.83.21 1.26.25.42.04.81-.15.95-.54l.8-2.18c.14-.38.56-.67.99-.63.12.01.23.03.35.03.12 0 .23-.02.35-.03.43-.04.85.25.99.63l.8 2.18c.14.39.53.58.95.54.43-.04.84-.13 1.26-.25.41-.12.69-.48.65-.9l-.22-2.29c-.04-.41.16-.85.55-1.02.12-.05.23-.11.34-.17.34-.17.82-.09 1.12.21l1.65 1.66c.3.3.75.32 1.08.06.32-.26.62-.55.88-.87.26-.33.24-.78-.06-1.08l-1.66-1.65c-.3-.3-.38-.78-.21-1.12.06-.11.12-.22.17-.34.17-.39.61-.59 1.02-.55l2.29.22c.42.04.78-.24.9-.65z'/%3E%3C/svg%3E")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    zIndex: 1,
                    transform: 'rotate(20deg)'
                }}
                animate={{
                    rotate: -340,
                    transition: {
                        duration: 30,
                        ease: "linear",
                        repeat: Infinity
                    }
                }}
            />
        
            <motion.div 
                className="exchange-card"
                variants={cardVariants}
                style={{
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* 卡片上的图案和效果 */}
                <motion.div 
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '2px solid rgba(52, 152, 219, 0.2)',
                        zIndex: 0
                    }}
                    variants={glowVariants}
                    animate="animate"
                />
                
                <motion.div 
                    style={{
                        position: 'absolute',
                        bottom: '15px',
                        left: '15px',
                        width: '30px',
                        height: '30px',
                        borderRadius: '3px',
                        background: 'rgba(52, 152, 219, 0.1)',
                        zIndex: 0
                    }}
                    variants={pulseVariants}
                    animate="animate"
                />
                
                <motion.h2 
                    variants={itemVariants}
                    className="card-title"
                >
                    <motion.span
                        style={{ display: 'inline-block' }}
                        animate={{
                            y: [0, -2, 0],
                            transition: {
                                duration: 1.5,
                                ease: "easeInOut",
                                repeat: Infinity,
                                repeatDelay: 3
                            }
                        }}
                    >
                        TRX
                    </motion.span>
                    {" "}
                    <motion.span
                        style={{ display: 'inline-block' }}
                        animate={{
                            y: [0, -2, 0],
                            transition: {
                                duration: 1.5,
                                ease: "easeInOut",
                                repeat: Infinity,
                                repeatDelay: 3,
                                delay: 0.2
                            }
                        }}
                    >
                        {t('energyExchangeTitle')}
                    </motion.span>
                </motion.h2>
                
                <motion.p 
                    className="exchange-description"
                    variants={itemVariants}
                >
                    {t('energyExchangeDescription')}
                </motion.p>
                
                <motion.div 
                    className="energy-options"
                    variants={itemVariants}
                >
                    {energyOptions.map((option, index) => (
                        <motion.div 
                            key={index}
                            className={`energy-option ${selectedOption === index ? 'selected' : ''}`}
                            onClick={() => handleSelectOption(index)}
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            <div className="option-price">{option.price} TRX</div>
                            <div className="option-energy">{option.energy.toLocaleString()} {t('energy')}</div>
                            <div className="option-description">{option.description}</div>
                            {selectedOption === index && (
                                <motion.div 
                                    className="option-selected-mark"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                        type: 'spring',
                                        stiffness: 300,
                                        damping: 15
                                    }}
                                >
                                    ✓
                                </motion.div>
                            )}
                        </motion.div>
                    ))}
                </motion.div>
                
                <motion.div className="option-tips" variants={itemVariants}>
                    <div className="tip-item">
                        <span className="tip-icon">📝</span>
                        <span className="tip-text">{t('energyTip1')}</span>
                    </div>
                    <div className="tip-item">
                        <span className="tip-icon">📝</span>
                        <span className="tip-text">{t('energyTip2')}</span>
                    </div>
                </motion.div>
                
                <motion.button 
                    className="transfer-button"
                    onClick={handleTransfer}
                    disabled={isLoading || selectedOption === null}
                    variants={buttonVariants}
                    whileHover={!isLoading ? "hover" : {}}
                    whileTap={!isLoading ? "tap" : {}}
                >
                    {isLoading ? (
                        <>
                            <motion.span
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                style={{ display: 'inline-block', marginRight: '10px' }}
                            >
                                ⚙️
                            </motion.span>
                            {t('processing')}
                        </>
                    ) : t('payNowForEnergy')}
                </motion.button>
                
                {transferResult && (
                    <motion.div 
                        className={`transfer-result ${transferResult.success ? 'success' : 'error'}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 15
                        }}
                    >
                        <div className="result-icon">
                            {transferResult.success ? '✅' : '❌'}
                        </div>
                        <div className="result-message">
                            {transferResult.message}
                        </div>
                        {transferResult.txID && (
                            <div className="result-txid">
                                {t('transactionId')}: {transferResult.txID.substring(0, 10)}...
                            </div>
                        )}
                    </motion.div>
                )}
                
                <motion.div 
                    className="exchange-help"
                    variants={itemVariants}
                >
                    <p>
                        <span className="help-icon">💡</span> 
                        {t('energyHelp')}
                    </p>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}

export default EnergyExchange; 